import { Award, Users, Clock, Laptop, CreditCard, GraduationCap } from 'lucide-react';
import { motion } from 'framer-motion';

const benefits = [
  {
    icon: Award,
    title: 'Certificación Oficial',
    description: 'Cursos acreditados y certificaciónes OS-10 reconocida a nivel nacional',
    accent: 'from-amber-500 to-orange-500',
  },
  {
    icon: Users,
    title: 'Docentes Expertos',
    description: 'Profesionales con amplia experiencia y dedicación en capacitación profesional',
    accent: 'from-blue-500 to-cyan-500',
  },
  {
    icon: Clock,
    title: 'Modalidad Flexible',
    description: 'Clases presenciales y online que se adaptan a tu horario y necesidades',
    accent: 'from-emerald-500 to-teal-500',
  },
  {
    icon: Laptop,
    title: 'Aula Virtual 24/7',
    description: 'Acceso permanente a materiales educativos desde cualquier dispositivo',
    accent: 'from-violet-500 to-purple-500',
  },
  {
    icon: CreditCard,
    title: 'Pagos Online',
    description: 'Facilidades de pago seguras con múltiples opciones de financiamiento',
    accent: 'from-rose-500 to-pink-500',
  },
  {
    icon: GraduationCap,
    title: 'Excelencia Educativa',
    description: 'Más de 5 años formando profesionales exitosos en la región',
    accent: 'from-blue-600 to-indigo-600',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function Benefits() {
  return (
    <section className="py-24 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-cyan-100/40 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-50/50 to-cyan-50/50 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-medium rounded-full mb-4">
            Nuestras Ventajas
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-6 font-heading">
            ¿Por Qué Elegir{' '}
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              Instituto Valparaíso
            </span>
            ?
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Nuestro compromiso es brindarte la mejor experiencia de aprendizaje
          </p>
        </motion.div>

        {/* Benefits grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group p-8 rounded-2xl bg-white border border-slate-100 shadow-lg shadow-slate-200/50 hover:shadow-2xl hover:shadow-blue-500/10 hover:border-blue-200 transition-all duration-300 relative overflow-hidden"
              >
                {/* Gradient accent line */}
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${benefit.accent} transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500`} />

                {/* Background glow on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/0 to-cyan-50/0 group-hover:from-blue-50/50 group-hover:to-cyan-50/50 transition-all duration-500" />

                <div className="relative z-10">
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${benefit.accent} mb-5 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-blue-700 transition-colors">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
